import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Edit3, Trash2, Save, Cpu } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";
const labelCls = "block text-xs font-medium text-slate-700 mb-1.5";

const SPORTS = [
  { id: "cricket", label: "Cricket" },
  { id: "baseball", label: "Baseball" },
  { id: "football", label: "Football" },
];

const blankMachine = {
  name: "",
  sport: "cricket",
  tagline: "",
  description: "",
  specs: ["", "", "", ""],
  image_url: "",
  sort_order: 0,
  is_active: true,
};

const MachineForm = ({ initial, onCancel, onSaved }) => {
  const padded = (a) => {
    const arr = [...(a || [])];
    while (arr.length < 4) arr.push("");
    return arr.slice(0, 4);
  };
  const [m, setM] = useState({
    name: initial?.name || "",
    sport: initial?.sport || "cricket",
    tagline: initial?.tagline || "",
    description: initial?.description || "",
    specs: padded(initial?.specs),
    image_url: initial?.image_url || "",
    sort_order: initial?.sort_order ?? 0,
    is_active: initial?.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const updateSpec = (i, v) => {
    const next = [...m.specs];
    next[i] = v;
    setM({ ...m, specs: next });
  };

  const save = async () => {
    if (!m.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const payload = {
        ...m,
        specs: m.specs.map((s) => s.trim()).filter(Boolean),
        sort_order: Number(m.sort_order) || 0,
        inventory_count: Math.max(0, Number(m.inventory_count) || 0),
      };
      if (isEdit) {
        await api.patch(`/admin/machines/${initial.id}`, payload, { withCredentials: true });
        toast.success("Machine updated.");
      } else {
        await api.post("/admin/machines", payload, { withCredentials: true });
        toast.success("Machine created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/40 rounded-lg p-5 shadow-card-hover space-y-4" data-testid="machine-form">
      <div className="font-semibold text-slate-900">{isEdit ? `Edit · ${initial.name}` : "New machine"}</div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="machine-input-name" className={inputCls} value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} placeholder="e.g. Bola Professional" />
        </div>
        <div>
          <label className={labelCls}>Sport</label>
          <select data-testid="machine-input-sport" className={inputCls} value={m.sport} onChange={(e) => setM({ ...m, sport: e.target.value })}>
            {SPORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Inventory (# of units)</label>
          <input data-testid="machine-input-inventory" type="number" min={0} max={99} className={inputCls} value={m.inventory_count} onChange={(e) => setM({ ...m, inventory_count: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Sort order</label>
          <input data-testid="machine-input-order" type="number" className={inputCls} value={m.sort_order} onChange={(e) => setM({ ...m, sort_order: e.target.value })} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Tagline (short headline)</label>
        <input data-testid="machine-input-tagline" className={inputCls} value={m.tagline} onChange={(e) => setM({ ...m, tagline: e.target.value })} placeholder="World-class bowling machine" />
      </div>

      <div>
        <label className={labelCls}>Description</label>
        <textarea data-testid="machine-input-desc" rows={3} className={`${inputCls} resize-none`} value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} />
      </div>

      <div>
        <label className={labelCls}>Specs (up to 4 short bullets)</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {m.specs.map((s, i) => (
            <input
              key={i}
              data-testid={`machine-input-spec-${i}`}
              className={inputCls}
              value={s}
              onChange={(e) => updateSpec(i, e.target.value)}
              placeholder={`Spec #${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>Image URL</label>
        <input data-testid="machine-input-image" className={inputCls} value={m.image_url} onChange={(e) => setM({ ...m, image_url: e.target.value })} placeholder="https://images.unsplash.com/..." />
        {m.image_url && (
          <img src={m.image_url} alt="Preview" className="mt-2 w-32 h-20 object-cover rounded border border-slate-200" />
        )}
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" data-testid="machine-input-active" checked={m.is_active} onChange={(e) => setM({ ...m, is_active: e.target.checked })} className="w-4 h-4 accent-spike-blue" />
        <span className="text-sm text-slate-700">Active (visible on homepage)</span>
      </label>

      <div className="flex gap-2">
        <button type="button" data-testid="machine-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button type="button" data-testid="machine-cancel" onClick={onCancel} className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-4 py-2 rounded-md">Cancel</button>
      </div>
    </div>
  );
};

const sportBadge = {
  cricket: "bg-emerald-50 text-emerald-700 border-emerald-200",
  baseball: "bg-amber-50 text-amber-700 border-amber-200",
  football: "bg-sky-50 text-sky-700 border-sky-200",
};

const AdminMachines = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/machines", { withCredentials: true });
      setItems(data);
    } catch {
      toast.error("Could not load machines.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Delete this machine?")) return;
    try {
      await api.delete(`/admin/machines/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-machines">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-spike-blue" /> Machines
          </h2>
          <p className="text-sm text-slate-600 mt-1">Bowling, pitching & rebound machines featured on the homepage. Toggle Active to show/hide.</p>
        </div>
        {!creating && !editing && (
          <button type="button" data-testid="machine-add-new" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4" /> New machine
          </button>
        )}
      </div>

      {creating && (
        <MachineForm
          initial={null}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload(); }}
        />
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-card" data-testid={`machine-card-${m.id}`}>
              {editing === m.id ? (
                <MachineForm
                  initial={m}
                  onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); reload(); }}
                />
              ) : (
                <div className="flex justify-between gap-4">
                  <div className="flex gap-4 min-w-0">
                    {m.image_url ? (
                      <img src={m.image_url} alt={m.name} className="w-24 h-20 object-cover rounded shrink-0" />
                    ) : (
                      <div className="w-24 h-20 bg-slate-100 rounded shrink-0 flex items-center justify-center text-slate-400 text-xs">no image</div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{m.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border capitalize ${sportBadge[m.sport]}`}>{m.sport}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {m.is_active ? "Active" : "Hidden"}
                        </span>
                        <span className="text-xs text-slate-400">#{m.sort_order}</span>
                      </div>
                      {m.tagline && <div className="text-sm text-slate-700 mt-1">{m.tagline}</div>}
                      {m.specs?.length > 0 && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-1">
                          {m.specs.slice(0, 3).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`machine-edit-${m.id}`} onClick={() => setEditing(m.id)} className="p-2 text-slate-600 hover:text-spike-blue rounded">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button type="button" data-testid={`machine-delete-${m.id}`} onClick={() => remove(m.id)} className="p-2 text-slate-600 hover:text-red-500 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="machine-empty">
              No machines yet. Create your first one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMachines;
